//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { IAutoApprovalModalState } from '@galleries/models/autoApprovalModalModel';
import { InstaShopAutoApprovalSources } from '@galleries/models/galleriesStateModel';
import { SliceNames } from '@redux/SliceNames';
import { crl8 } from 'app/curalate.globals';

export const initialState: IAutoApprovalModalState = {
  isOpen: false,
  isAutoApprovalEnabled: false,
  selectedAutoApprovalStrategy: InstaShopAutoApprovalSources.allMedia,
  previousAutoApprovalStrategy: undefined,
  showProductWarning: false,
  productWarningDismissed: false,
  isApplyToAllGalleriesSelected: false,
};

const { reducer, actions } = createSlice({
  name: SliceNames.GalleriesAutoApprovalModal,
  initialState,
  reducers: {
    openModal: (state) => {
      state.isOpen = true;
    },

    closeModal: (state) => {
      state.isApplyToAllGalleriesSelected = false;
      state.isOpen = false;
    },

    setAutoApprovalEnabled: (
      state,
      action: PayloadAction<{ isAutoApprovalEnabled: boolean }>,
    ) => {
      state.isAutoApprovalEnabled = action.payload.isAutoApprovalEnabled;
    },

    toggleApplyToAllGalleriesCheckbox: (state) => {
      state.isApplyToAllGalleriesSelected = !state.isApplyToAllGalleriesSelected;
    },

    setAutoApprovalStrategy: (
      state,
      action: PayloadAction<{
        selectedAutoApprovalStrategy: InstaShopAutoApprovalSources;
      }>,
    ) => {
      const { selectedAutoApprovalStrategy } = action.payload;

      const { previousAutoApprovalStrategy, productWarningDismissed } = state;

      const isChangingAutoApprovalSetting =
        previousAutoApprovalStrategy &&
        previousAutoApprovalStrategy !== selectedAutoApprovalStrategy;

      const showProductWarning = (() => {
        switch (selectedAutoApprovalStrategy) {
          case InstaShopAutoApprovalSources.allMedia:
            return false;
          case InstaShopAutoApprovalSources.productTaggedMedia:
            // Verify that we're actually switching from what was previously set
            // Verify that the warning wasn't previously dismissed
            return isChangingAutoApprovalSetting && !productWarningDismissed;
          default:
            crl8.logger.cloud.error(
              `Unrecognized selectedAutoApprovalStrategy: ${selectedAutoApprovalStrategy}`,
            );
            break;
        }
      })();

      state.selectedAutoApprovalStrategy = selectedAutoApprovalStrategy;
      state.showProductWarning = !!showProductWarning;
    },

    setPreviousAutoApprovalStrategy: (
      state,
      action: PayloadAction<{
        autoApprovalStrategy: InstaShopAutoApprovalSources;
      }>,
    ) => {
      const { autoApprovalStrategy } = action.payload;
      state.previousAutoApprovalStrategy = autoApprovalStrategy;
    },

    dismissProductWarning: (state) => {
      state.showProductWarning = false;
      state.productWarningDismissed = true;
    },

    resetModal: () => initialState,
  },
});

const {
  openModal,
  closeModal,
  setAutoApprovalStrategy,
  setAutoApprovalEnabled,
  toggleApplyToAllGalleriesCheckbox,
  setPreviousAutoApprovalStrategy,
  dismissProductWarning,
  resetModal,
} = actions;

export const autoApprovalModalActions = {
  openModal,
  closeModal,
  setAutoApprovalStrategy,
  setAutoApprovalEnabled,
  toggleApplyToAllGalleriesCheckbox,
  dismissProductWarning,
  setPreviousAutoApprovalStrategy,
  resetModal,
};

export default reducer;
